import { booleanAttribute, ChangeDetectionStrategy, Component, computed, contentChildren, ElementRef, inject, input, linkedSignal, signal } from '@angular/core'
import { toSignal } from '@angular/core/rxjs-interop'
import { map } from 'rxjs'

import { MatButtonModule } from '@angular/material/button'
import { MatIconModule } from '@angular/material/icon'

import { Card } from '../card/card'
import { ResponsiveUi } from '../responsive-ui'
import { Scroll } from '../scroll'

@Component({
	selector: 'app-card-group',
	imports: [MatButtonModule, MatIconModule],
	templateUrl: './card-group.html',
	styleUrl: './card-group.scss',
	host: {
		'[aria-labelledby]': '"card-group-title-" + instanceNumber()',
		role: 'region',
		'[class.open]': 'open()',
	},
	changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CardGroup {
	// services/dependencies
	private readonly hostRef = inject<ElementRef<HTMLElement>>(ElementRef)
	protected readonly responsiveUi = inject(ResponsiveUi)
	private readonly scroll = inject(Scroll)

	// inputs/outputs
	readonly header = input.required<string>()
	readonly headerColor = input('white', { alias: 'header-color' })
	readonly cardCount = input<number>(0, { alias: 'card-count' })
	readonly isCardInGroupHighlighted = input(false, { alias: 'is-card-in-group-highlighted', transform: booleanAttribute })
	readonly groupOf = input<'relationships'|'interactions'>('interactions', { alias: 'group-of' })

	// queries
	readonly cards = contentChildren(Card)

	// state
	readonly open = linkedSignal(() => this.isCardInGroupHighlighted() || !this.responsiveUi.isSmallViewport())
	protected readonly showExpandAllCardsButton = computed(() => !this.cards().every(card => card.open()))
	protected readonly showCollapseAllCardsButton = computed(() => !this.cards().every(card => !card.open()))
	protected readonly instanceNumber = signal<number|undefined>(undefined)
	protected readonly maxGroupHeight = computed(() => {
		// use card height (the taller of card/row) so the group always expands tall enough for both views
		if (this.open()) return this.cardCount() * 285 + 59
		else return 0
	})
	protected readonly scrollingUp = toSignal(this.scroll.scrollDirection$.pipe(map(scrollDir => scrollDir === 'up')))

	// for assigning a unique ID to elements in each instance of this component
	static instanceCount = 0

	constructor() {
		this.instanceNumber.set(CardGroup.instanceCount++)
	}

	/** Toggles the collapse/expand state of the group and scrolls the group into view on small viewports when expanded. */
	protected toggleGroupCollapseExpand(): void {
		this.open.set(!this.open())
		setTimeout(() => {
			if (this.responsiveUi.isSmallViewport() && this.open()) {
				this.hostRef.nativeElement.scrollIntoView({ behavior: 'smooth', block: 'nearest' })
			}
		}, 500) // wait for the CSS transition to complete
	}

	/** Expands or collapses all cards in the group. */
	protected expandOrCollapseAllCards(cardsOpen: boolean): void {
		this.cards().forEach(card => card.open.set(cardsOpen))
	}

}
